import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Shield, FileText, Mail } from 'lucide-react';
import { images } from '../data/mock';

const CONTACT_EMAIL = 'everything@8eight8.net';

// Map every legal-route alias to a canonical SEO target.
// All these routes render the same LegalPage component; canonical tells Google which URL is the "master".
const ROUTE_META = {
  '/privacy': {
    canonical: 'https://www.alohatequilafest.com/privacy',
    title: 'Privacy Policy | Aloha Tequila & Arts Festival 2026',
    description: 'Privacy Policy for Aloha Tequila & Arts Festival — how we handle your data, cookies, and contact information.'
  },
  '/terms': {
    canonical: 'https://www.alohatequilafest.com/terms',
    title: 'Terms & Conditions | Aloha Tequila & Arts Festival 2026',
    description: 'Terms & Conditions for Aloha Tequila & Arts Festival ticket purchase, entry, and event participation.'
  },
  '/refund-policy': {
    canonical: 'https://www.alohatequilafest.com/refund-policy',
    title: 'Refund Policy | Aloha Tequila & Arts Festival 2026',
    description: 'Refund Policy for Aloha Tequila & Arts Festival — tickets are non-refundable but transferable to a guest aged 21+.'
  },
  '/legal': {
    canonical: 'https://www.alohatequilafest.com/legal',
    title: 'Privacy Policy & Terms | Aloha Tequila & Arts Festival 2026',
    description: 'Privacy Policy, Terms & Conditions, and Refund Policy for Aloha Tequila & Arts Festival.'
  }
};

const LegalPage = () => {
  const { pathname } = useLocation();
  const meta = ROUTE_META[pathname] || ROUTE_META['/legal'];

  return (
    <div className="min-h-screen bg-gray-950" data-testid="legal-page">
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
      </Helmet>

      <section className="relative py-12 sm:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-pink-500/5 via-transparent to-transparent" />
        
        <div className="max-w-4xl mx-auto px-4 relative z-10">
          {/* Back to Home */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-white/70 hover:text-pink-400 transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          {/* Header */}
          <div className="text-center mb-10">
            <img 
              src={images.logo} 
              alt="Aloha Tequila & Arts Festival" 
              className="h-24 sm:h-32 mx-auto mb-6 object-contain"
            />
            <h1 className="text-3xl sm:text-4xl font-black text-white mb-2">
              Legal Information
            </h1>
            <p className="text-white/60">Privacy Policy & Terms and Conditions</p>
          </div>

          {/* Business Information — Meta Ads compliance */}
          <div className="bg-gradient-to-br from-pink-500/10 to-orange-500/5 border border-pink-500/30 rounded-2xl p-6 sm:p-8 mb-8" data-testid="legal-business-info">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">Business Information</h2>
            <div className="space-y-3 text-white/80 text-sm sm:text-base">
              <p><strong className="text-white">Festival name:</strong> Aloha Tequila & Arts Festival</p>
              <p><strong className="text-white">Presented by:</strong> Aloha Cancer Project, a Honolulu-based 501(c)(3) nonprofit organization</p>
              <p><strong className="text-white">Event venue:</strong> International Market Place, 2330 Kalakaua Ave, Honolulu, HI 96815</p>
              <p><strong className="text-white">Authorized contact:</strong> Aloha Tequila Fest Organizing Team</p>
              <p><strong className="text-white">Phone:</strong> <a href="tel:+18083012134" className="text-pink-400 hover:text-pink-300 underline">808-301-2134</a></p>
              <p><strong className="text-white">Email:</strong> <a href={`mailto:${CONTACT_EMAIL}`} className="text-pink-400 hover:text-pink-300 underline">{CONTACT_EMAIL}</a></p>
              <p><strong className="text-white">Website:</strong> <a href="https://www.alohatequilafest.com" className="text-pink-400 hover:text-pink-300 underline">www.alohatequilafest.com</a></p>
            </div>
          </div>

          {/* Privacy Policy Section */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 sm:p-8 mb-8">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-white/10">
              <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-orange-500 rounded-xl flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white">Privacy Policy</h2>
            </div>

            <div className="space-y-6 text-white/80 text-sm sm:text-base leading-relaxed">
              <div>
                <h3 className="text-white font-semibold text-lg mb-2">Information We Collect</h3>
                <p className="mb-3">
                  <strong className="text-pink-400">Personal Information:</strong> We collect personal information such as your name, email address, phone number, and postal address when you register for the Aloha Tequila & Arts Festival.
                </p>
                <p>
                  <strong className="text-pink-400">Payment Information:</strong> We do not directly collect payment information. Payment transactions are securely processed by our banking institution.
                </p>
              </div>

              <div>
                <h3 className="text-white font-semibold text-lg mb-2">How We Use Your Information</h3>
                <p className="mb-2">We use your personal information to:</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Process your registration for the event.</li>
                  <li>Communicate with you about the event and related updates.</li>
                  <li>Provide customer support and respond to inquiries.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-white font-semibold text-lg mb-2">Information Sharing</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>We do not share or sell your personal information to third parties.</li>
                  <li>Your personal information is securely stored and accessible only to authorized personnel who need it to perform their duties.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-white font-semibold text-lg mb-2">Data Security</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>We take appropriate measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.</li>
                  <li>Our website and our payment processing systems are secured with industry-standard encryption and security protocols.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-white font-semibold text-lg mb-2">Your Choices</h3>
                <p>You may update or correct your personal information by contacting us directly.</p>
              </div>

              <div>
                <h3 className="text-white font-semibold text-lg mb-2">Changes to This Policy</h3>
                <p>We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of any material changes by posting the updated policy on our website.</p>
              </div>

              <div className="pt-4 border-t border-white/10">
                <p className="text-white/60 text-sm">
                  <strong>Effective Date:</strong> January 1, 2026
                </p>
                <p className="mt-2 text-white/60 text-sm">
                  By purchasing a ticket for the event, you agree to the terms of this Privacy Policy. Aloha Tequila Fest reserves the right to modify or update this Privacy Policy at any time. Please review this policy periodically for changes.
                </p>
              </div>
            </div>
          </div>

          {/* Terms and Conditions Section */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 sm:p-8 mb-8">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-white/10">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-xl flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white">Terms and Conditions</h2>
            </div>

            <div className="space-y-6 text-white/80 text-sm sm:text-base leading-relaxed">
              <div>
                <h3 className="text-white font-semibold text-lg mb-2">1. No Return or Refund Policy</h3>
                <p>
                  All ticket sales are final. The Aloha Tequila Festival does not offer refunds for tickets once they have been purchased. Your ticket is not refundable, but your original ticket can be transferred to someone else — who must be 21 or older and show a valid government-issued ID at the door. Refunds will not be issued for denied entry due to invalid identification, intoxication, removal for misconduct, or no-shows. In the event of a force-majeure cancellation by the organizer, ticket holders will be notified via the email address provided at purchase. If you have any questions about this policy please contact us at <a href={`mailto:${CONTACT_EMAIL}`} className="text-pink-400 hover:text-pink-300 underline">{CONTACT_EMAIL}</a> before completing your purchase.
                </p>
              </div>

              <div>
                <h3 className="text-white font-semibold text-lg mb-2">2. Age Restriction & Responsible Consumption</h3>
                <p>
                  The Aloha Tequila Festival is strictly for individuals who are <strong className="text-pink-400">21 years of age or older</strong>. All attendees must show a valid government-issued ID to verify their age upon entry. Failure to provide proper identification will result in denied entry without a refund. Guests are encouraged to <strong className="text-white">drink responsibly</strong>, pace tasting pours, hydrate between samples, and arrange safe transportation home (rideshare, designated driver, or walking from a nearby Waikiki hotel). Festival staff reserve the right to decline service to any guest who appears intoxicated. If you or someone you know needs help with alcohol use, the SAMHSA National Helpline is available 24/7 at <a href="tel:1-800-662-4357" className="text-pink-400 hover:text-pink-300 underline">1-800-662-HELP (4357)</a>.
                </p>
              </div>

              <div>
                <h3 className="text-white font-semibold text-lg mb-2">3. Event Changes</h3>
                <p>
                  We reserve the right to make changes to the event, including but not limited to changes in date, time, location, and content. Any such changes will be communicated to ticket holders via the contact information provided at the time of purchase.
                </p>
              </div>

              <div>
                <h3 className="text-white font-semibold text-lg mb-2">4. Conduct and Liability</h3>
                <p>
                  Attendees are expected to conduct themselves in a manner that does not interfere with the enjoyment of the event by other participants. We reserve the right to refuse entry or eject any person who is deemed to be acting in a disruptive or inappropriate manner, without refund or compensation.
                </p>
              </div>

              <div>
                <h3 className="text-white font-semibold text-lg mb-2">5. Limitation of Liability</h3>
                <p>
                  To the fullest extent permitted by applicable law, the Aloha Tequila Festival shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses, resulting from your attendance at the event.
                </p>
              </div>

              <div className="pt-4 border-t border-white/10">
                <p className="text-white/60 text-sm">
                  <strong>Effective Date:</strong> January 1, 2026
                </p>
                <p className="mt-2 text-white/60 text-sm">
                  By purchasing a ticket, you acknowledge that you have read, understood, and agreed to these terms and conditions. These terms and conditions are subject to change without prior notice. Please review them regularly to stay informed of any updates.
                </p>
                <p className="mt-2 text-white/50 text-xs italic">
                  Note: This information is provided for informational purposes only and does not constitute legal advice. It is recommended to consult with a legal professional to ensure compliance with local laws and regulations.
                </p>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="bg-gradient-to-r from-pink-500/10 to-orange-500/10 border border-pink-500/20 rounded-2xl p-6 text-center">
            <h3 className="text-white font-bold text-lg mb-2">Questions or Concerns?</h3>
            <p className="text-white/70 text-sm mb-4">
              If you have any questions about our Privacy Policy, Terms and Conditions, or our data practices, please contact us:
            </p>
            <a 
              href={`mailto:${CONTACT_EMAIL}`}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 text-white font-bold px-6 py-3 rounded-full transition-all duration-300 hover:scale-105"
            >
              <Mail className="w-5 h-5" />
              {CONTACT_EMAIL}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LegalPage;
